import { CompStructure } from '@wix/document-services-types';
import {
  AiMediaData,
  AiMediaItem,
  createFlatStructureMap,
  getImgObj,
} from '@wix/editor-content-injector';
import _ from 'lodash';
import { ExtendedContentTypes } from '../types';
import {
  DEFAULT_IMAGE_ASPECT_RATIO,
  DEFAULT_IMAGE_RESOLUTION,
  IMAGE_ASPECT_RATIO_BY_FIELD_ROLE,
  IMAGE_RESOLUTION_BY_FIELD_ROLE,
  ScenePromptIds,
  WIDE_LANDSCAPE_IMAGE_ASPECT_RATIO,
  WIDE_LANDSCAPE_IMAGE_RESOLUTION,
} from './consts';
import {
  AbstractSectionName,
  DefaultSectionName,
  FIELD_ROLES,
  ImageDescription,
  ImageDetails,
  ImagesMap,
  UploadImagesDetails,
} from './types';

export const getImagesDataFromStructure = (
  presetStructure: CompStructure,
): ImagesMap => {
  const flatCompStructureMap = createFlatStructureMap(presetStructure);
  return getImagesData(flatCompStructureMap);
};

export const getImagesData = (
  flatCompStructureMap: CompStructure[],
): ImagesMap => {
  const imagesMap: ImagesMap = new Map();

  flatCompStructureMap.forEach((compStructure) => {
    const imageObject = getImgObj(compStructure);
    if (!imageObject) {
      return;
    }

    const fieldRole = compStructure.contentRole?.fieldRole || '';
    const isWideLandscape =
      fieldRole === FIELD_ROLES.CROPTYPE_LANDSCAPE &&
      compStructure.layout?.width === 980;
    const aspectRatio = isWideLandscape
      ? WIDE_LANDSCAPE_IMAGE_ASPECT_RATIO
      : IMAGE_ASPECT_RATIO_BY_FIELD_ROLE[fieldRole] ||
        DEFAULT_IMAGE_ASPECT_RATIO;
    const imageResolution = isWideLandscape
      ? WIDE_LANDSCAPE_IMAGE_RESOLUTION
      : IMAGE_RESOLUTION_BY_FIELD_ROLE[fieldRole] || DEFAULT_IMAGE_RESOLUTION;
    if (compStructure.id) {
      imagesMap.set(compStructure.id, {
        ...imageResolution,
        aspectRatio,
        fieldRole,
      });
    }
  });

  return imagesMap;
};

function isDefaultSectionName(value: string): value is DefaultSectionName {
  return Object.values(DefaultSectionName).includes(
    value as DefaultSectionName,
  );
}

function containsAbstract(str: string): boolean {
  const regex = /(ABSTRACT|abstract)/;
  return regex.test(str);
}

export const getScenePromptId = (sectionName: string, groupName: string) => {
  if (containsAbstract(groupName)) {
    return ScenePromptIds[AbstractSectionName.ABSTRACT];
  } else if (
    !isDefaultSectionName(sectionName) ||
    sectionName === (ExtendedContentTypes.Header as string)
  ) {
    return ScenePromptIds[DefaultSectionName.GENERAL];
  } else {
    return ScenePromptIds[sectionName];
  }
};

export const getImagesDescriptions = (
  imagesMap: ImagesMap,
): Record<string, ImageDescription> => {
  return Array.from(imagesMap.keys()).reduce((acc, compId) => {
    acc[compId] = {};
    return acc;
  }, {});
};

export const getGroupedMapByCropType = (
  imagesMap: ImagesMap,
  sectionName: string,
): Map<string, ImagesMap> => {
  const groupedMap = new Map();

  imagesMap.forEach((item, key) => {
    const { fieldRole } = item;
    const croptype =
      fieldRole === FIELD_ROLES.ABSTRACT
        ? FIELD_ROLES.CROPTYPE_ABSTRACT
        : fieldRole;
    const groupName = `${sectionName}_${croptype}`;

    const items = groupedMap.get(groupName) ?? new Map();
    items.set(key, item);
    groupedMap.set(groupName, items);
  });

  return groupedMap;
};

export const getLastNMapItems = <K, V>(
  map: Map<K, V>,
  n: number,
): Map<K, V> => {
  const lastNEntries = _.takeRight(Array.from(map.entries()), n);
  return new Map(lastNEntries);
};

export const getUploadImagesDataFromStructure = async (
  presetStructure: CompStructure,
  pageName: string,
  sectionTitle: string,
): Promise<UploadImagesDetails[]> => {
  const flatCompStructureMap = createFlatStructureMap(presetStructure);
  const imagesMap: UploadImagesDetails[] = [];

  for (const compStructure of flatCompStructureMap) {
    const compId = compStructure.id;
    const imageObject = getImgObj(compStructure);
    if (!imageObject || !compId) {
      continue;
    }

    try {
      const fileName = `${pageName}-${sectionTitle}-${imagesMap.length + 1}`;
      const wixStaticBaseUrl = 'https://static.wixstatic.com';
      const { height, width, uri } = imageObject;
      imagesMap.push([
        compId,
        {
          height,
          width,
          ...(!uri.startsWith(wixStaticBaseUrl)
            ? { localUri: uri }
            : { staticUri: uri }),
          displayName: fileName,
        },
      ]);
    } catch (error) {
      console.error(
        `Failed to fetch and process image for ${compStructure.id}:`,
        error,
      );
    }
  }

  return imagesMap;
};

export const imageDetailsToAiMediaItem = ({
  localUri,
  staticUri,
  ...data
}: ImageDetails): AiMediaItem => ({
  ...data,
  uri: staticUri ?? (localUri as string),
});

export const createAiMediaData = (
  images: Record<string, ImageDetails>,
): AiMediaData => {
  return Object.keys(images).reduce(
    (acc, key) => ({
      ...acc,
      [key]: imageDetailsToAiMediaItem(images[key]),
    }),
    {},
  );
};
